import React, { useState, useEffect, useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import {
  Modal,
  Paper,
  Fade,
  IconButton,
  Box,
  Button,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

import axios from "axios";
import { URL } from "../config";
import toast from "react-hot-toast";
import AddCustomerReviews from "./AddCustomerReviews";

const CustomerReviewsList = () => {
  const [dataSource, setDataSource] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editData, setEditData] = useState(null);

  const fetchAll = async () => {
    try {
      const res = await axios.get(`${URL}/customervideoreviews`);
      setDataSource(res.data.data || []);
    } catch {
      toast.error("Failed to fetch reviews");
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleEdit = (data) => {
    setEditData(data);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this review?"))
      return;

    try {
      await axios.delete(`${URL}/customervideoreviews/${id}`);
      toast.success("Review deleted successfully!");
      fetchAll();
    } catch {
      toast.error("Failed to delete review");
    }
  };

  const columns = useMemo(() => [
    {
      accessorKey: "actions",
      header: "Actions",
      Cell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton size="small" onClick={() => handleEdit(row.original)}>
            <EditIcon sx={{ color: "#174253" }} fontSize="small" />
          </IconButton>

          <IconButton
            size="small"
            onClick={() => handleDelete(row.original.reviewId)}
          >
            <DeleteIcon sx={{ color: "#d32f2f" }} fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
    { accessorKey: "reviewId", header: "ID" },
    { accessorKey: "reviewTitle", header: "Review Title" },
    {
      accessorKey: "reviewVideo",
      header: "Video",
      Cell: ({ row }) =>
        row.original.reviewVideo ? (
          <video
            src={`${URL}${row.original.reviewVideo}`}
            width="80"
            height="60"
            controls
            style={{ borderRadius: 8 }}
          />
        ) : (
          "No Video"
        ),
    },
  ], []);

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box
          sx={{
            fontWeight: 700,
            fontSize: 22,
            color: "#174253",
            borderLeft: "5px solid #174253",
            pl: 1.5,
          }}
        >
          Customer Reviews List
        </Box>

        <Button
          sx={{
            borderRadius: 25,
            background: "linear-gradient(90deg, #0b8793, #174253)",
            color: "white",
          }}
          onClick={() => {
            setEditData(null);
            setModalVisible(true);
          }}
        >
          Add Review
        </Button>
      </Box>

         <MaterialReactTable
        columns={columns}
        data={[...dataSource].reverse()}
      
        enableColumnActions={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableColumnFilters={false}
        enableHiding={false}
        enableGlobalFilter={false}
      
        muiTablePaperProps={{
          sx: { boxShadow: "none", background: "#fff", borderRadius: "15px" },
        }}
      
        muiTableContainerProps={{
          sx: {
            backgroundColor: "#fff",
            width: "100%",
            overflowX: "auto",
            borderRadius: "12px",
            "&::-webkit-scrollbar": { height: "6px" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#e0e3ea",
              borderRadius: "10px",
            },
          },
        }}
      
        muiTableHeadCellProps={{
          sx: {
            backgroundColor: "#e0e0e0ff",
            color: "#393e4f",
            fontWeight: 600,
            fontSize: "16px",
            borderBottom: "0.5px solid #e3e5eb",
            borderRight: "none",
            borderLeft: "none",
            textAlign: "left",
            height: "63px",
            paddingTop: "23px",
            marginTop: "15px",
          },
        }}
      
        muiTableBodyRowProps={{
          sx: {
            backgroundColor: "#fff",
            "&:hover": { backgroundColor: "#eff6fe" },
            "& td": {
              fontSize: { xs: "12px", sm: "14px" },
              p: "8px",
              borderBottom: "0.5px solid #e3e5eb",
              borderRight: "none",
              borderLeft: "none",
              height: "54px",
            },
          },
        }}
      
        initialState={{
          pagination: {
            pageIndex: 0,
            pageSize: 100, 
          },
        }}
      />

      <Modal open={modalVisible} onClose={() => setModalVisible(false)}>
        <Fade in={modalVisible}>
          <Paper
            sx={{
              width: "100vw",
              height: "100vh",
              bgcolor: "transparent",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <AddCustomerReviews
              review={editData}
              onClose={() => setModalVisible(false)}
              refresh={fetchAll}
            />
          </Paper>
        </Fade>
      </Modal>
    </Box>
  );
};

export default CustomerReviewsList;